import { useState } from "react";

import { useViewportInfoContext } from "@/src/contexts";
import type { IDiscountsPanel, IDiscountsPanelResult } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import { AnimationApi, AnimationType } from "@Components/AnimationApi";
import type { IMap } from "@Components/Map";
import { Map, MapMarker } from "@Components/Map";
import { urlFormat } from "@Components/Modules/Helpers";
import type { ISearchBoxResultItem } from "@Components/SearchBox";

import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import { MapLink } from "../common/links/MapLink";
import type { ISvgIconLink } from "../common/links/SvgIconLink";
import DiscountsPanel from "../discount/DiscountsPanel";
import type { IPlaceSearch } from "../location/PlaceSearch";
import { PlaceSearch } from "../location/PlaceSearch";
import type { IMapMarker } from "../map/MapMarker";
import { MapMarkerInfo } from "../map/MapMarker";
import { MapMobile } from "../map/MapMobile";
import type { IShoppingMallMapMarker } from "../map/ShoppingMallMapMarker";
import { ShoppingMallMapMarkerInfo } from "../map/ShoppingMallMapMarker";
import type { IMomentHeader } from "./MomentHeader";
import { MomentHeader } from "./MomentHeader";
import type { IMomentMapBrandsFilter } from "./MomentMapBrandsFilter";
import { MomentMapBrandsFilter } from "./MomentMapBrandsFilter";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./Moment.module.css";

export interface IMomentMap {
    category: string;
    contentContainers?: IContentContainer;
    filters?: IMomentMapBrandsFilter;
    header: IMomentHeader;
    mapLinkButton: ISvgIconLink;
    map: IMap;
    mobileMapTitle: string;
    noResultText: string;
    onMapRefreshed: null;
    onMapToggle: () => void;
    placeDescription: string;
    placeId: string;
    showMapStart: boolean;
    searchLocation: IPlaceSearch;
    discountsPanel: IDiscountsPanel;
}

interface IMomentMapState {
    showMap: boolean;
    category: string;
    placeId: string;
    placeDescription: string;
    noResultContent: boolean;
    placeSearch?: IPlaceSearch;
}

const renderMarker = (props: IMapMarker) => {
    let renderInfoMarker: ((props: IMapMarker) => JSX.Element) | undefined;

    if (props.markerType === 1) {
        renderInfoMarker = (mProps: IShoppingMallMapMarker) => (
            <ShoppingMallMapMarkerInfo {...mProps.info} key={mProps.id} />
        );
    }

    if (props.markerType === 2) {
        renderInfoMarker = (mProps: IMapMarker) => <MapMarkerInfo {...mProps.info} key={mProps.id} />;
    }

    return <MapMarker {...props} renderInfoContent={renderInfoMarker} key={props.id} />;
};

const mapComponent = (
    props: IMomentMap,
    state: IMomentMapState,
    toggleMap: () => void,
    isMobile: boolean,
) => {
    if (!props.map || state.showMap === false) {
        return null;
    }

    const map = {
        ...props.map,
        renderMarker,
        categoryName: state.category,
        placeId: state.placeId,
    };

    const mapKey =
        state.category && state.placeId
            ? `${state.category}-${state.placeId}`
            : state.category || state.placeId;

    if (!isMobile) {
        return <Map {...map} key={mapKey} />;
    }

    return <MapMobile title={props.mobileMapTitle} map={map} onClose={toggleMap} key={mapKey} />;
};

export const MomentMap = (props: IMomentMap) => {
    const { isMobile } = useViewportInfoContext();

    const [state, setState] = useState({
        category: props.category,
        placeId: props.placeId,
        placeDescription: props.placeDescription,
        noResultContent: false,
        showMap: props.showMapStart && !isMobile,
        placeSearch: isMobile === false && props.searchLocation ? { ...props.searchLocation } : undefined,
    });

    const toggleMap = (): void => setState(() => ({ ...state, showMap: !state.showMap }));

    const categoryChange = (category: string) => setState({ ...state, category, noResultContent: false });

    const panelResult = (response: IDiscountsPanelResult) =>
        (!response ||
            !response.carousels ||
            response.carousels.length === 0 ||
            response.carousels[0].discounts.length === 0) &&
        setState(() => ({ ...state, noResultContent: true }));

    const updatePlaceId = (
        _event: React.MouseEvent<HTMLAnchorElement>,
        item: ISearchBoxResultItem,
    ): boolean => {
        setState({ ...state, placeId: item.text });
        return true;
    };

    return (
        <div className={css.momentMapComponent}>
            <MomentHeader {...props.header} />
            <div className={cssPortal.standardContainer}>
                <div className={css.contentBlock}>
                    {(!props.showMapStart || isMobile) && (
                        <div className={css.mapLink}>
                            <MapLink {...props.mapLinkButton} onClick={toggleMap} />
                        </div>
                    )}
                    {state.placeSearch && !isMobile && (
                        <div className={css.mapSearchBox}>
                            <PlaceSearch
                                {...state.placeSearch}
                                onSelect={updatePlaceId}
                                value={state.placeDescription}
                            />
                        </div>
                    )}
                    <AnimationApi animationType={AnimationType.Fade} duration={300}>
                        {mapComponent(props, state, toggleMap, isMobile)}
                    </AnimationApi>
                    {props.filters && (
                        <MomentMapBrandsFilter
                            {...props.filters}
                            selectedCategory={state.category}
                            onFilterChange={categoryChange}
                        />
                    )}
                    {(state.noResultContent && <div className={css.noData}>{props.noResultText}</div>) || (
                        <DiscountsPanel
                            {...props.discountsPanel}
                            key={state.placeId + state.category}
                            ajaxAction={urlFormat(props.discountsPanel.ajaxAction, {
                                categoryName: state.category,
                                placeId: state.placeId,
                                group: !isMobile,
                            })}
                            success={panelResult}
                        />
                    )}
                </div>
                <ContentContainer {...props.contentContainers} />
            </div>
        </div>
    );
};
